/**
 * GmailHelpdeskCard — workspace dashboard widget for the stateful
 * Helpdesk Agent. Three states:
 *   1. Not configured (no GOOGLE_CLIENT_ID on backend) → instructional
 *      banner with link to setup docs.
 *   2. Configured + not connected → "Connect Gmail" CTA.
 *   3. Configured + connected → stats (email / drafts / last poll)
 *      + "Poll now" + "Disconnect" buttons.
 *
 * No auth required (yet) — workspace id is a soft URL secret like
 * the rest of the dashboard.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, RefreshCw, Plug, Unplug, CheckCircle2, AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { API } from "../../config";

export default function GmailHelpdeskCard({ workspaceId, accent, accentBg, accentBorder }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [recentDrafts, setRecentDrafts] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/status`, { params: { workspace_id: workspaceId } });
      setStatus(r.data);
      if (r.data.connected) {
        const d = await axios.get(`${API}/oauth/gmail/drafts`, { params: { workspace_id: workspaceId, limit: 5 } });
        setRecentDrafts(d.data?.drafts || []);
      } else {
        setRecentDrafts([]);
      }
    } catch (e) {
      console.error("gmail status", e);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Read ?gmail=connected|error from URL and toast once
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const g = sp.get("gmail");
    if (g === "connected") {
      toast.success("Gmail pripojený. Helpdesk Agent začne čoskoro generovať drafty.");
      // Clean URL
      sp.delete("gmail");
      const newUrl = window.location.pathname + (sp.toString() ? "?" + sp.toString() : "");
      window.history.replaceState({}, "", newUrl);
    } else if (g === "error") {
      const reason = sp.get("reason") || "unknown";
      toast.error(`Pripojenie Gmail zlyhalo: ${reason}`);
      sp.delete("gmail"); sp.delete("reason");
      const newUrl = window.location.pathname + (sp.toString() ? "?" + sp.toString() : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const connect = async () => {
    try {
      const r = await axios.get(`${API}/oauth/gmail/login`, { params: { workspace_id: workspaceId } });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      if (e.response?.status === 503) {
        toast.error("Gmail integrácia nie je nakonfigurovaná na backende.");
      } else {
        toast.error("Pripojenie zlyhalo.");
      }
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Odpojiť Gmail účet? Drafty pre nové emaily sa prestanú generovať.")) return;
    try {
      await axios.post(`${API}/oauth/gmail/disconnect`, null, { params: { workspace_id: workspaceId } });
      toast.success("Gmail odpojený.");
      await load();
    } catch {
      toast.error("Odpojenie zlyhalo.");
    }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await axios.post(`${API}/oauth/gmail/poll-now`, null, { params: { workspace_id: workspaceId } });
      const d = r.data;
      if (d.status === "ok") {
        toast.success(`Skenovaných ${d.scanned} správ · ${d.drafts_created} nových draftov`);
      } else {
        toast.error(`Polling: ${d.status}`);
      }
      await load();
    } catch (e) {
      toast.error("Manuálny poll zlyhal.");
    } finally {
      setPolling(false);
    }
  };

  if (loading) return null;

  // STATE 1: Not configured on backend
  if (status && !status.configured) {
    return (
      <section className="mb-8" data-testid="gmail-helpdesk-card">
        <h2 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-gray-400 mb-3">
          <Mail className="w-3.5 h-3.5" /> Helpdesk · Gmail integrácia
        </h2>
        <div className="p-5 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-200 mb-1">Gmail OAuth nie je nakonfigurovaný</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                Backend potrebuje <code className="text-amber-300">GOOGLE_CLIENT_ID</code> a <code className="text-amber-300">GOOGLE_CLIENT_SECRET</code> z Google Cloud Console (Gmail API enabled, redirect URI <code className="text-amber-300">/api/oauth/gmail/callback</code>). Po pridaní do <code>backend/.env</code> a reštarte sa Helpdesk Agent prepne na stateful režim — bude periodicky čítať inbox a vytvárať drafty odpovedí na schválenie.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // STATE 2: Configured + not connected
  if (status && !status.connected) {
    return (
      <section className="mb-8" data-testid="gmail-helpdesk-card">
        <h2 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-gray-400 mb-3">
          <Mail className="w-3.5 h-3.5" /> Helpdesk · Gmail integrácia
        </h2>
        <div className="p-6 border bg-black" style={accentBorder}>
          <div className="flex items-start gap-4">
            <div className="p-2 border" style={accentBorder}>
              <Mail className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-black mb-1">Aktivuj stateful Helpdesk Agenta</p>
              <p className="text-xs text-gray-400 mb-4 leading-relaxed max-w-xl">
                Pripoj svoj support Gmail a agent začne čítať nové emaily, klasifikovať ich (return / refund / shipping / …) a vytvárať pre-vyplnené <span className="text-gray-200 font-semibold">drafty odpovedí</span>. <span className="text-gray-200 font-semibold">Nikdy neodošle email sám</span> — vždy ich pred odoslaním schvaľuješ ty v Gmail-e.
              </p>
              <button
                onClick={connect}
                data-testid="gmail-connect-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-black font-black uppercase tracking-widest text-xs"
                style={accentBg}
              >
                <Plug className="w-4 h-4" /> Pripojiť Gmail
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // STATE 3: Connected
  const lastPoll = status?.last_poll_at ? new Date(status.last_poll_at) : null;
  const lastPollHuman = lastPoll
    ? `pred ${Math.max(1, Math.round((Date.now() - lastPoll.getTime()) / 60000))} min`
    : "nikdy";

  return (
    <section className="mb-8" data-testid="gmail-helpdesk-card">
      <h2 className="text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2 text-gray-400 mb-3">
        <Mail className="w-3.5 h-3.5" /> Helpdesk · Gmail integrácia
      </h2>
      <div className="p-6 border bg-black" style={accentBorder}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-[11px] uppercase tracking-widest text-emerald-400 font-bold">Pripojené</p>
              <p className="text-sm text-gray-200 font-semibold" data-testid="gmail-connected-email">{status.email || "—"}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={pollNow}
              disabled={polling}
              data-testid="gmail-poll-now-btn"
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-700 hover:border-gray-500 text-xs font-bold uppercase tracking-wider text-gray-200 disabled:opacity-50"
            >
              {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Skenuj teraz
            </button>
            <button
              onClick={disconnect}
              data-testid="gmail-disconnect-btn"
              className="inline-flex items-center gap-2 px-3 py-2 border border-red-900/60 hover:border-red-700 text-xs font-bold uppercase tracking-wider text-red-300"
            >
              <Unplug className="w-3.5 h-3.5" /> Odpojiť
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat label="Drafty vytvorené" value={status.drafts_created_total} accent={accent} testid="gmail-stat-drafts" />
          <Stat label="Polly spustené" value={status.polls_total} accent={accent} testid="gmail-stat-polls" />
          <Stat label="Posledný poll" value={lastPollHuman} accent={accent} testid="gmail-stat-last-poll" small />
          <Stat label="Auto-interval" value="10 min" accent={accent} testid="gmail-stat-interval" small />
        </div>

        {status.refresh_error && (
          <div className="p-3 border border-red-900/60 bg-red-950/30 text-xs text-red-300 mb-4">
            <strong>Refresh chyba:</strong> {status.refresh_error}. Skús odpojiť a znova pripojiť Gmail.
          </div>
        )}

        {recentDrafts.length > 0 && (
          <div className="border-t border-gray-900 pt-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Posledné drafty
            </p>
            <div className="space-y-2">
              {recentDrafts.map((d) => (
                <div key={d.draft_id || d.message_id} data-testid="gmail-draft-row" className="flex items-center gap-3 text-xs">
                  <SeverityBadge severity={d.severity} />
                  <span className="text-gray-300 truncate flex-1">{d.subject || "(bez predmetu)"}</span>
                  {d.escalation_needed && (
                    <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      Escalate
                    </span>
                  )}
                  <span className="text-gray-600 text-[10px] tabular-nums shrink-0">
                    {d.ticket_category || "—"}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-3 italic">
              Otvor Gmail → priečinok "Drafts" pre kontrolu a odoslanie. Agent nikdy neodosiela sám.
            </p>
          </div>
        )}

        {recentDrafts.length === 0 && status.polls_total > 0 && (
          <p className="text-xs text-gray-500 italic">
            Zatiaľ žiadne drafty. Pri ďalšom skenovaní (auto každých 10 min) agent vyhľadá nové neprečítané emaily.
          </p>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent, testid, small }) {
  return (
    <div className="p-3 border border-gray-900 bg-neutral-950" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</p>
      <p className={`${small ? "text-sm" : "text-2xl"} font-black tabular-nums`} style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const map = {
    critical: { bg: "bg-red-500/20", text: "text-red-300", border: "border-red-500/30" },
    high:     { bg: "bg-orange-500/20", text: "text-orange-300", border: "border-orange-500/30" },
    medium:   { bg: "bg-yellow-500/20", text: "text-yellow-300", border: "border-yellow-500/30" },
    low:      { bg: "bg-emerald-500/20", text: "text-emerald-300", border: "border-emerald-500/30" },
  };
  const s = map[severity] || map.medium;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${s.bg} ${s.text} border ${s.border} shrink-0 w-16 text-center`}>
      {severity || "—"}
    </span>
  );
}
